import React, { useState, useEffect } from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';

interface ExchangeRates {
  USD_EUR: number;
  CAD_EUR: number;
  timestamp: number;
}

interface CurrencyRatesProps {
  compact?: boolean;
}

const CurrencyRates: React.FC<CurrencyRatesProps> = ({ compact = false }) => {
  const [rates, setRates] = useState<ExchangeRates | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchRates();
    // Update rates every 30 minutes
    const interval = setInterval(fetchRates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      // Using exchangerate-api.com (free tier)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('API response not ok');
      }
      
      const data = await response.json();
      
      if (data.rates) {
        setRates({
          USD_EUR: 1 / data.rates.USD, // Convert to EUR per USD
          CAD_EUR: 1 / data.rates.CAD, // Convert to EUR per CAD
          timestamp: Date.now()
        });
        setError(false);
      } else {
        throw new Error('No rates in response');
      }
    } catch (err) {
      // Silently use fallback rates - don't log to avoid script errors
      setError(true);
      // Fallback rates (approximate current rates)
      setRates({
        USD_EUR: 0.95,
        CAD_EUR: 0.68,
        timestamp: Date.now()
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !rates) {
    if (compact) {
      return <span className="text-gray-400 text-sm">Loading rates...</span>;
    }
    return (
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  // Compact mode - inline display
  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex items-center">
          <span className="text-lg mr-1">🇺🇸</span>
          <span className="text-xs text-gray-500 mr-1">USD</span>
          <span className="font-bold text-gray-900">{rates?.USD_EUR.toFixed(2)} €</span>
        </div>
        <div className="flex items-center">
          <span className="text-lg mr-1">🇨🇦</span>
          <span className="text-xs text-gray-500 mr-1">CAD</span>
          <span className="font-bold text-gray-900">{rates?.CAD_EUR.toFixed(2)} €</span>
        </div>
      </div>
    );
  }

  // Full mode - card display
  return (
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
      <div className="flex items-center mb-4">
        <CurrencyDollarIcon className="w-6 h-6 mr-2" />
        <h3 className="text-lg font-semibold">Live Exchange Rates</h3>
      </div>
      
      <div className="space-y-3">
        {/* USD to EUR */}
        <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl font-bold mr-2">🇺🇸</span>
              <div>
                <p className="text-sm opacity-90">US Dollar</p>
                <p className="text-xs opacity-75">USD → EUR</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">
                {rates?.USD_EUR.toFixed(4)} <span className="text-base">€</span>
              </p>
              <p className="text-xs opacity-75">per $1</p>
            </div>
          </div>
        </div>

        {/* CAD to EUR */}
        <div className="bg-white bg-opacity-10 rounded-lg p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl font-bold mr-2">🇨🇦</span>
              <div>
                <p className="text-sm opacity-90">Canadian Dollar</p>
                <p className="text-xs opacity-75">CAD → EUR</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">
                {rates?.CAD_EUR.toFixed(4)} <span className="text-base">€</span>
              </p>
              <p className="text-xs opacity-75">per C$1</p>
            </div>
          </div>
        </div>
      </div>

      {/* Last updated */}
      <div className="mt-4 pt-3 border-t border-white border-opacity-20">
        <p className="text-xs opacity-75 text-center">
          {error && '⚠️ Using fallback rates • '}
          Updated: {rates ? new Date(rates.timestamp).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : ''}
        </p>
      </div>
    </div>
  );
};

export default CurrencyRates;
